import React, { useEffect, useRef, useState, useCallback } from "react";
import moment from "moment";
import { useIntl } from "react-intl";

import { useSelector, useDispatch } from "react-redux";

import { calculateElementPosition } from "../../../../utils/calculateElementPosition";

import {
    ADD_INTERNAL_CHAT_GROUP_MESSAGES,
    SEND_INTERNAL_CHAT_MESSAGE,
    UPDATE_INTERNAL_CHAT_MESSAGING_INFO,
} from "../../../../helpers/internalChatConstants";

import useChatDispatchActions from '../../../../customHooks/useChatDispatchActions';
import useResizableWindow from "../../../../customHooks/useResizableWindow";

import { checkPermissions } from "../../../../components/ctvoicer/accessControl/AccessControl";
import { getLocalTime } from "../../../../utils/dateUtility";
import { uuidv4 } from "../../../../utils/uuidGenerator";
import {
    GROUP_SEND_BLOCKS,
    getApiErrorMessage,
    getGroupSendBlock,
    resolveSentGroupMessage
} from "../../../../utils/internalChatGroups";
import { messageGroupsService } from "../../../../newServices/corporateChat";

import internalChatToast from "../../utils/internalChatToast";

import InternalAttachmentErrors from "../attachments/InternalAttachmentErrors";
import InternalDroppableFiles from "../attachments/InternalDroppableFiles";
import InternalPreviewFilesSender from "../attachments/InternalPreviewFilesSender";

import Footer from "./components/Footer";
import Header from "./components/Header";
import Body from "./components/Body";
import GroupHeader from "./components/GroupHeader";
import GroupBody from "./components/GroupBody";

import { setWindowHierarchy } from "../../../../redux/actions/systemConfig/systemConfig";

import "./chat-window.scss";

const minWindowSize = {
    width: 330,
    height: 400
}

/** A mesma janela abre a conversa individual (`user`) e o grupo (`group`): a posição,
 *  o tamanho e a área de soltar arquivos são os mesmos, e só o conteúdo muda.
 */
const ChatWindow = ({ position, user, group, closeChatWindow, terminateUserChat, openGroupManagement }) => {

    const intl = useIntl();

    const chatWindowRef = useRef(null);

    const resizeHandles = {
        left: useRef(),
        bottom: useRef()
    }

    const dispatchChatActions = useChatDispatchActions()
    const dispatch = useDispatch();


    const contacts = useSelector(state => state.internalChat.contacts);
    const activeChat= useSelector(state => state.internalChat.activeChat);
    const windowHierarchy = useSelector(state => state.systemConfig.windowHierarchy);
    const activeChatUserId = activeChat?.user?.userId || null;
    const activeConversationKey = activeChatUserId || (activeChat?.group ? `group-${activeChat.group}` : null);

    const [windowPosition, setWindowPosition] = useState({
        x: 0,
        y: 0,
        alignment: null
    })

    const { windowWidth, windowHeight, onPositionChangeHandler } = useResizableWindow({
        windowSize: localStorage.getItem("internalChatWindowSize") ? JSON.parse(localStorage.getItem("internalChatWindowSize")) : null,
        minWindowSize,
        resizeHandles,
        windowRef: chatWindowRef,
    });

    const [messageText, setMessageText] = useState("");

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [attachmentErrors, setAttachmentErrors] = useState([]);

    const userData = useSelector(state => state.auth.userData);

    const isFinished = React.useMemo(() => {
        return contacts.find(contact => contact.id === activeChat?.user?.id)?.isFinished || false;
    }, [contacts, activeChat]);

    const groupSendBlock = React.useMemo(() => {
        return group ? getGroupSendBlock(group, userData?.id) : null;
    }, [group, userData]);

    /** No lugar do campo de envio aparece o motivo do bloqueio. */
    const blockedMessage = groupSendBlock === GROUP_SEND_BLOCKS.DELETED ?
        intl.formatMessage({ id: "Este grupo foi excluído. O histórico continua disponível." }) :
        groupSendBlock === GROUP_SEND_BLOCKS.VIEW_ONLY ?
            intl.formatMessage({ id: "Você só pode visualizar as mensagens deste grupo." }) :
            null;

    const canAttachFiles = React.useMemo(() => {
        return checkPermissions(userData, ["attach.files"]) && !isFinished && !groupSendBlock;
    }, [userData, isFinished, groupSendBlock]);

    const groupId = group?.id;

    /** A resposta já é a mensagem gravada, com a data do servidor e os anexos com `url`:
     *  ela entra na conversa sem nova busca e vira a última mensagem do grupo.
     */
    const handleSendGroupMessage = useCallback(async ({ messageText, externalReference, files }) => {
        if (!groupId) return;
        try {
            const res = await messageGroupsService.postMessageGroupMessages(groupId, {
                messageText: messageText || '',
                externalReference: externalReference || uuidv4(),
                files: files
            });
            const sentMessage = resolveSentGroupMessage(res && res.data);
            if (!sentMessage) throw new Error('Mensagem não retornada pela API');

            dispatchChatActions(ADD_INTERNAL_CHAT_GROUP_MESSAGES, { id: groupId, messages: [sentMessage] });
        } catch (error) {
            internalChatToast.error(
                getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível enviar a mensagem. Tente novamente." })
            );
        }
    }, [groupId, dispatchChatActions, intl]);

    const handleAddAttachments = useCallback((files) => {
        setAttachmentFiles(prevFiles => [...prevFiles, ...files]);
    }, []);

    const handleClearAttachments = useCallback(() => {
        setAttachmentFiles([]);
    }, []);

    const handleSendAttachments = useCallback((text) => {
        const message = {
            createdAt: getLocalTime(moment()),
            messageText: text,
            externalReference: uuidv4(),
            files: attachmentFiles
        }
        if (groupId) {
            handleSendGroupMessage(message)
        } else {
            dispatchChatActions(SEND_INTERNAL_CHAT_MESSAGE, message)
        }
        setAttachmentFiles([]);
    }, [attachmentFiles, dispatchChatActions, groupId, handleSendGroupMessage]);

    /** Os anexos pendentes são por conversa: sem isto, trocar de contato levaria os
     *  arquivos escolhidos para a conversa errada.
     */
    useEffect(() => {
        setAttachmentFiles([]);
        setAttachmentErrors([]);
    }, [activeConversationKey]);

    const handlePriorizeWindow = useCallback(() => {
        if(windowHierarchy !== 'internal-chat') dispatch(setWindowHierarchy('internal-chat'));
    }, [windowHierarchy])


    useEffect(() => {
        // if (activeChat) {
        //     if(activeChat?.user?.id){
        //         dispatch(setWindowHierarchy('internal-chat'))
        //     } else {
        //         dispatch(setWindowHierarchy(null))
        //     }
        // } else {
        //     setWindowPosition({
        //         x: 0,
        //         y: 0,
        //         alignment: null
        //     })
        // }
        if(activeConversationKey){
            dispatch(setWindowHierarchy('internal-chat'))
        } else {
            dispatch(setWindowHierarchy(null))
            setWindowPosition({
                x: 0,
                y: 0,
                alignment: null
            })
        }
    }, [activeConversationKey])

    const handleWindoPosition = (width, height) => {
        return calculateElementPosition({
            containerWidth: width,
            containerHeight: height,
            x: position.x,
            y: position.y
        });

    }

    useEffect(() => {
        if (activeChat) {
            const delayDebounceFn = setTimeout(() => {
                dispatchChatActions(UPDATE_INTERNAL_CHAT_MESSAGING_INFO, messageText)
            }, 150)

            return () => clearTimeout(delayDebounceFn)
        }
    }, [messageText])

    useEffect(() => {
        const { width, height } = chatWindowRef.current.getBoundingClientRect();
        const { top, left, alignment } = handleWindoPosition(width, height);

        setWindowPosition({
            x: left - 15,
            y: alignment.y === "top" ? top - 7 : top + position.height + 7,
            alignment: alignment.y
        });
        onPositionChangeHandler()
    }, [position]);

    useEffect(() => {
        const { top, left, alignment } = handleWindoPosition(windowWidth, windowHeight);

        setWindowPosition({
            x: left - 15,
            y: alignment.y === "top" ? top - 7 : top + position.height + 7,
            alignment: alignment.y
        });

        localStorage.setItem("internalChatWindowSize", JSON.stringify({
            width: windowWidth,
            height: windowHeight
        }))

    }, [windowWidth, windowHeight])

    return (
        <div
            ref={chatWindowRef}
            onClick={handlePriorizeWindow}
            className={`chat-window-ui ${(user || group) && (windowPosition.y > 0 || windowPosition.x > 0) ? "show" : ""} ${windowPosition.alignment || ""}`}
            style={{
                top: /*windowPosition.y*/ 25,
                left: windowPosition.x,
                width: windowWidth,
                height: windowHeight,
            }}>

            <div ref={resizeHandles.left} className="left-handler" />
            <div ref={resizeHandles.bottom} className="bottom-handler" />

            {/* O droppeable envolve a janela inteira — inclusive com o preview aberto —
                porque é o `onDragOver` com preventDefault que impede o navegador de abrir
                o arquivo solto e derrubar a SPA junto com a conversa. */}
            <InternalDroppableFiles
                disabled={!canAttachFiles}
                onSelectFiles={handleAddAttachments}
                onErrors={setAttachmentErrors}
            >
                {group ?
                    <GroupHeader
                        group={group}
                        closeChatWindow={closeChatWindow}
                        openGroupManagement={openGroupManagement} /> :
                    <Header
                        user={user}
                        isFinished={isFinished}
                        terminateUserChat={terminateUserChat}
                        closeChatWindow={closeChatWindow} />
                }

                {group ?
                    <GroupBody group={group} /> :
                    <Body
                        setMessageText={setMessageText}
                        setShowEmojiPicker={setShowEmojiPicker}
                        showEmojiPicker={showEmojiPicker} />
                }

                <InternalAttachmentErrors
                    errors={attachmentErrors}
                    onDismiss={() => setAttachmentErrors([])}
                />

                <Footer
                    isFinished={isFinished}
                    blockedMessage={blockedMessage}
                    showEmojiPicker={showEmojiPicker}
                    setShowEmojiPicker={setShowEmojiPicker}
                    user={user}
                    messageText={messageText}
                    setMessageText={setMessageText}
                    canAttachFiles={canAttachFiles}
                    onSelectFiles={handleAddAttachments}
                    onAttachmentErrors={setAttachmentErrors}
                    onSendMessage={groupId ? handleSendGroupMessage : undefined}
                />

                {attachmentFiles.length > 0 && (
                    <InternalPreviewFilesSender
                        files={attachmentFiles}
                        onChangeFiles={setAttachmentFiles}
                        onClose={handleClearAttachments}
                        onSend={handleSendAttachments}
                        onErrors={setAttachmentErrors}
                    />
                )}
            </InternalDroppableFiles>
        </div>
    );
}

export default React.memo(ChatWindow);